import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { QuarterlyPeriodChartData } from '@app-types/performanceTrends';
import type { ChartEventHandlers } from '@app-types/chart';
import BaseEChart from './BaseEChart';
import { buildQuarterlyPeriodOption, quarterlyGridLeft } from './quarterlyPeriodOptions';
import {
  anchorQuarterlyViewport,
  quarterlyViewportFromEvent,
  quarterlyVisibleCount,
  resolveQuarterlyViewport,
  type QuarterlyViewportAnchor,
} from './quarterlyViewport';

export interface QuarterlyTrendChartProps {
  data: QuarterlyPeriodChartData;
  height?: number;
}

/**
 * One bar per quarter, with an X-only slider so the Y-axis stays visible.
 */
const QuarterlyTrendChart: React.FC<QuarterlyTrendChartProps> = ({
  data,
  height = 229,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<QuarterlyViewportAnchor | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const measure = () => {
      const next = node.getBoundingClientRect().width;
      if (next > 0) setWidth((current) => current === next ? current : next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const visibleCount = quarterlyVisibleCount(width, quarterlyGridLeft(data), data.points.length);
  const viewport = resolveQuarterlyViewport(data.points, visibleCount, anchorRef.current);
  const option = useMemo(
    () => buildQuarterlyPeriodOption(data, { start: viewport.start, end: viewport.end }),
    [data, viewport.start, viewport.end],
  );
  const onEvents = useMemo<ChartEventHandlers>(() => ({
    datazoom: (event) => {
      const next = quarterlyViewportFromEvent(event, data.points, visibleCount);
      // ECharts handles the live drag. Remember its range without setOption
      // during pointer movement: BaseEChart uses notMerge, which restarts it.
      if (next) anchorRef.current = anchorQuarterlyViewport(data.points, next);
    },
  }), [data.points, visibleCount]);

  return (
    <div ref={containerRef} style={{ width: '100%', minWidth: 0 }}>
      <BaseEChart
        option={option}
        height={height}
        onEvents={onEvents}
        ariaLabel={`${data.title}の四半期棒グラフ`}
      />
    </div>
  );
};

export default QuarterlyTrendChart;
